import { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import { authAPI } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock, Eye, EyeOff, Sun, Moon, CheckCircle } from 'lucide-react';
import { MetaTradifyLogo } from '@/components/MetaTradifyLogo';
import { toast } from 'sonner';

export default function ResetPasswordPage() {
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [form, setForm] = useState({ token, new_password: '', confirm_password: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showPass, setShowPass] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.token) { toast.error('Missing reset token'); return; }
    if (form.new_password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (form.new_password !== form.confirm_password) { toast.error('Passwords do not match'); return; }
   if (!/^(?=.*[A-Za-z])(?=.*\d)(?=.*[^A-Za-z0-9]).{6,}$/.test(form.password.trim())) {
  toast.error('Password must have letter, number & special character');
  return;
}
    setLoading(true);
    try {
      await authAPI.resetPassword({ token: form.token, new_password: form.new_password });
      setSuccess(true);
      toast.success('Password reset successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[var(--bg-primary)] flex items-center justify-center px-4" data-testid="reset-password-page">
      <button onClick={toggleTheme} data-testid="reset-theme-toggle" className="absolute top-4 right-4 p-2 rounded-lg bg-[var(--hover-bg)] border border-[var(--border-subtle)] transition-all z-10">
        {theme === 'dark' ? <Sun className="w-4 h-4 text-amber-400" /> : <Moon className="w-4 h-4 text-green-500" />}
      </button>
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/3 left-1/4 w-80 h-80 bg-green-500/5 rounded-full blur-[100px]" />
      </div>
      <div className="w-full max-w-[400px] relative">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
              <MetaTradifyLogo size={32} />
            <span className="text-lg font-bold font-['Outfit'] text-[var(--text-primary)]">metaTradify</span>
          </Link>
          <h1 className="text-2xl font-bold font-['Outfit'] tracking-tight text-[var(--text-primary)]">Set New Password</h1>
          <p className="text-sm text-[var(--text-secondary)] mt-1">Choose a strong password for your account</p>
        </div>

        {!success ? (
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-password-form">
            {!token && (
              <div className="space-y-2">
                <Label htmlFor="token" className="text-sm text-[var(--text-secondary)]">Reset Token</Label>
                <Input id="token" data-testid="reset-token-input" placeholder="Paste your reset token"
                  value={form.token} onChange={(e) => setForm({ ...form, token: e.target.value })}
                  className="bg-[var(--bg-surface)] border-[var(--border-subtle)] text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:border-green-500 h-11 font-mono text-xs" />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="new_password" className="text-sm text-[var(--text-secondary)]">New Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-tertiary)]" />
                <Input id="new_password" type={showPass ? 'text' : 'password'} data-testid="reset-new-password-input"
                  placeholder="Min 6 characters" value={form.new_password}
                  onChange={(e) => setForm({ ...form, new_password: e.target.value })}
                  className="bg-[var(--bg-surface)] border-[var(--border-subtle)] text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:border-green-500 h-11 pl-10 pr-10" />
                <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]">
                  {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm" className="text-sm text-[var(--text-secondary)]">Confirm Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-tertiary)]" />
                <Input id="confirm" type={showPass ? 'text' : 'password'} data-testid="reset-confirm-password-input"
                  placeholder="Repeat your password" value={form.confirm_password}
                  onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
                  className="bg-[var(--bg-surface)] border-[var(--border-subtle)] text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:border-green-500 h-11 pl-10" />
              </div>
            </div>
            <button type="submit" disabled={loading} data-testid="reset-submit-btn"
              className="w-full h-11 bg-green-500 hover:bg-green-600 text-white rounded text-sm font-medium transition-all duration-200 disabled:opacity-50">
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>
        ) : (
          <div className="text-center space-y-4" data-testid="reset-success">
            <div className="w-14 h-14 mx-auto rounded-full bg-emerald-500/15 flex items-center justify-center">
              <CheckCircle className="w-7 h-7 text-emerald-400" />
            </div>
            <p className="text-sm text-[var(--text-primary)] font-medium">Password Reset Successful</p>
            <p className="text-sm text-[var(--text-secondary)]">You can now sign in with your new password.</p>
            <button onClick={() => navigate('/login')} data-testid="go-to-login-btn"
              className="px-6 py-2.5 bg-green-500 hover:bg-green-600 text-white rounded text-sm font-medium transition-all">
              Go to Login
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
